import type { GuestDataProps } from "@/components/GuestForm";
import { ReservationSummaryCard } from "@/components/checkin/ReservationSummaryCard";
import { RoomCard } from "@/components/checkin/RoomCard";
import { Alert, AlertDescription, AlertIcon, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { useEliteReservation } from "@/contexts/EliteTemporaryContextProvider";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import type { WebCheckInReservationData } from "@/types/Reservation";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  FileCheck2,
  Hotel,
  Loader2,
  Send,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Mantieni export per compatibilità con EliteTemporaryContextProvider e altri componenti
export type GuestState = Record<number, GuestDataProps[]>;

export default function CheckIn() {
  const { t } = useTranslation("checkIn");
  useDocumentTitle("Fast Check-in");
  const { checkIn, reservation, isLoading } = useEliteReservation();
  const [visitReason, setVisitReason] = useState<string>("1");
  const [draftGuests, setDraftGuests] = useState<GuestState>({});
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const reservationData = reservation as WebCheckInReservationData | null;
  const rooms = reservationData?.reservation?.rooms || [];

  const allRoomsCompleted =
    rooms.length > 0 &&
    rooms.every(
      (r) => (r.checkedInGuests?.length || 0) >= (r.guestsNumber || 1),
    );

  // Se la prenotazione è già completamente registrata, reindirizza o mostra completamento
  useEffect(() => {
    if (reservationData?.isFullyCompleted && allRoomsCompleted) {
      navigate("/completed", { replace: true });
    }
  }, [reservationData?.isFullyCompleted, allRoomsCompleted]);

  // Conteggi complessivi
  const totalRegisteredCount =
    reservationData?.totalCheckedInGuests ??
    rooms.reduce((acc, r) => acc + (r.checkedInGuests?.length || 0), 0);

  const totalExpectedCount =
    reservationData?.totalExpectedGuests ??
    rooms.reduce((acc, r) => acc + (r.guestsNumber || 0), 0);

  const pendingDraftCount = Object.values(draftGuests).reduce(
    (acc, arr) => acc + (arr?.length || 0),
    0,
  );

  const handleAddDraftGuest = (roomIdx: number, newGuest: GuestDataProps) => {
    setDraftGuests((prev) => {
      const roomDrafts = prev[roomIdx] || [];
      return {
        ...prev,
        [roomIdx]: [...roomDrafts, newGuest],
      };
    });
  };

  const handleUpdateDraftGuest = (
    roomIdx: number,
    guestIdx: number,
    updatedGuest: GuestDataProps,
  ) => {
    setDraftGuests((prev) => {
      const roomDrafts = prev[roomIdx] || [];
      return {
        ...prev,
        [roomIdx]: roomDrafts.map((g, i) =>
          i === guestIdx ? updatedGuest : g,
        ),
      };
    });
  };

  const handleRemoveDraftGuest = (roomIdx: number, guestIdx: number) => {
    setDraftGuests((prev) => {
      const roomDrafts = prev[roomIdx] || [];
      return {
        ...prev,
        [roomIdx]: roomDrafts.filter((_, i) => i !== guestIdx),
      };
    });
  };

  const handleCheckInSubmit = () => {
    if (pendingDraftCount === 0) {
      toast.error(
        t("addAtLeastOne", "Inserisci almeno un ospite prima di procedere."),
        {
          richColors: true,
        },
      );
      return;
    }

    setLoading(true);
    checkIn(draftGuests, Number.parseInt(visitReason))
      .then((updatedReservation) => {
        setDraftGuests({});
        toast.success(
          t("messages.success", "Check-in completato con successo!"),
          {
            richColors: true,
            position: "top-center",
          },
        );

        const updatedRooms = updatedReservation?.reservation?.rooms || [];
        const updatedAllRoomsDone =
          updatedRooms.length > 0 &&
          updatedRooms.every(
            (r: any) =>
              (r.checkedInGuests?.length || 0) >= (r.guestsNumber || 1),
          );

        // Se tutti gli ospiti sono stati completati
        if (updatedReservation?.isFullyCompleted && updatedAllRoomsDone) {
          navigate("/completed");
        }
      })
      .catch((err) => {
        console.error("Check-in error:", err);
        toast.error(
          t(
            "errorMessage",
            "Si è verificato un errore durante il check-in. Riprova.",
          ),
          {
            richColors: true,
          },
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (isLoading || !reservation) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
        <Spinner className="size-8 text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">
          {t("loadingReservation", "Caricamento dati prenotazione...")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-24 md:pb-12">
      {/* Intestazione Pagina */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 sm:pt-1 pb-4 border-b">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight flex items-center gap-2.5">
            <Sparkles className="size-6 text-primary" />
            {t("title", "Fast Check-in Online")}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {t(
              "subtitle",
              "Registra gli ospiti della tua prenotazione prima dell'arrivo per accedere direttamente in camera.",
            )}
          </p>
        </div>
      </div>

      {/* Avviso Documenti / Frase Introduttiva (Bug 1219509298518707) */}
      <Alert className="bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200">
        <AlertIcon>
          <AlertCircle className="size-4 text-amber-600 dark:text-amber-400" />
        </AlertIcon>
        <AlertTitle className="text-xs sm:text-sm font-medium">
          {t(
            "foreignNationalityWarning",
            "Per i cittadini extra-UE utilizzare solo passaporto. Il documento utilizzato in fase di check-in deve essere presentato in struttura.",
          )}
        </AlertTitle>
      </Alert>

      {/* Se la prenotazione è già completamente registrata */}
      {reservationData?.isFullyCompleted && (
        <Alert className="bg-emerald-500/10 border-emerald-500/20 text-emerald-800 dark:text-emerald-300">
          <CheckCircle2 className="size-5 text-emerald-600" />
          <AlertTitle className="text-base font-bold">
            {t("allRegistered", "Tutti gli ospiti sono stati registrati!")}
          </AlertTitle>
          <AlertDescription className="text-xs sm:text-sm mt-1 flex items-center justify-between gap-2">
            <span>
              {t(
                "checkInComplete",
                "Il check-in per questa prenotazione è già completo.",
              )}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/completed")}
              className="text-xs shrink-0"
            >
              {t("seeSummary", "Vedi Riepilogo →")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Layout a 2 colonne su Desktop / Colonna Singola su Mobile */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Colonna Camere & Ospiti (Larghezza 2/3 su desktop) */}
        <div className="md:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Hotel className="size-5 text-primary" />
              {t("roomsAndOccupants", "Camere & Occupanti")} ({rooms.length})
            </h2>
            <span className="text-xs text-muted-foreground">
              {t("ofInserted", {
                current: totalRegisteredCount + pendingDraftCount,
                total: totalExpectedCount,
                defaultValue: `${totalRegisteredCount + pendingDraftCount} di ${totalExpectedCount} inseriti`,
              })}
            </span>
          </div>

          <div className="space-y-4">
            {rooms.map((room, rIdx) => (
              <RoomCard
                key={room.id || room.idRisPren || rIdx}
                room={room}
                roomIndex={rIdx}
                draftGuests={draftGuests[rIdx] || []}
                onAddDraftGuest={handleAddDraftGuest}
                onUpdateDraftGuest={handleUpdateDraftGuest}
                onRemoveDraftGuest={handleRemoveDraftGuest}
              />
            ))}
          </div>

          {/* Alert informativo se non è stato inserito ancora nessun ospite */}
          {totalRegisteredCount === 0 && pendingDraftCount === 0 && (
            <Alert className="bg-muted/30 border-muted-foreground/20 text-xs text-muted-foreground">
              <AlertTitle className="font-semibold text-foreground">
                {t(
                  "startByFillingTitle",
                  "Inizia compilando i dati per ciascuna camera",
                )}
              </AlertTitle>
              <AlertDescription>
                {t(
                  "startByFillingDesc",
                  "Clicca su \"+ Inserisci Dati Ospite\" nella camera desiderata. Puoi caricare le foto del documento per compilare i campi automaticamente.",
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Colonna Laterale: Riepilogo Soggiorno (Larghezza 1/3 su desktop) */}
        <div className="md:col-span-1">
          <div className="sticky top-20">
            <ReservationSummaryCard
              reservationData={reservationData!}
              visitReason={visitReason}
              onVisitReasonChange={setVisitReason}
              pendingDraftCount={pendingDraftCount}
              totalRegisteredCount={totalRegisteredCount}
              totalExpectedCount={totalExpectedCount}
              loading={loading}
              onSubmitCheckIn={handleCheckInSubmit}
            />
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar per Mobile */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-background/95 backdrop-blur-md border-t p-3 sm:hidden shadow-lg">
        <div className="container mx-auto flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <span className="text-xs text-muted-foreground">
              {pendingDraftCount > 0
                ? t(
                    pendingDraftCount === 1
                      ? "newReady_one"
                      : "newReady_other",
                    {
                      count: pendingDraftCount,
                      defaultValue: `${pendingDraftCount} ${pendingDraftCount === 1 ? "nuovo pronto" : "nuovi pronti"}`,
                    },
                  )
                : t("registeredCount", {
                    current: totalRegisteredCount,
                    total: totalExpectedCount,
                    defaultValue: `${totalRegisteredCount}/${totalExpectedCount} registrati`,
                  })}
            </span>
            <p className="text-xs font-bold text-foreground">
              {pendingDraftCount > 0
                ? t("readyToSend", "Pronto da inviare")
                : t("waitingForData", "In attesa di dati")}
            </p>
          </div>

          <Button
            size="lg"
            className="font-bold gap-2 px-5 shadow-md cursor-pointer"
            disabled={pendingDraftCount === 0 || loading}
            onClick={handleCheckInSubmit}
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("submitting", "Invio...")}
              </>
            ) : (
              <>
                <Send className="size-4" />
                {t("submitButton", "Invia Check-in")}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
